/**
 * 節點 × 品牌可用性矩陣 — effective 狀態純函數（P1 唯讀）
 *
 * 設計依據：docs/operations/backoffice-redesign-2026-06-27.md §5.4 / §5.6 / §7.2。
 *
 * 這裡的判定刻意「鏡像」後端 `ip.service.getEnabledIPs()`（APP `/api/ip` 的真實下發
 * gate 順序）：isEnabled → minTier → isDecoy → allowedBrands 可見性 → tier allowedNodeIds
 * → nodeType(sk6/vless) → healthStatus=down → single-inbound-sk6 後置過濾。
 * P1 為唯讀，純前端依既有 admin API（/api/admin/nodes + /api/admin/tiers）的欄位
 * 在前端重算 effective 顯示，不做任何寫入。
 *
 * 🔴 落地紅線：
 *  - `allowedBrands` 為 **fail-OPEN**：NULL / 空陣列＝對「全部品牌」可見。判定一律走
 *    `isBrandAllowed()` 語意，NULL 不算「不可見」、也不算「漂移」。
 *  - 不可只回三態 false-green：除 可用 / 可見未選 / 品牌不可見 外，必須把 health=down /
 *    minTier 太高 / isDecoy / nodeType / single-inbound-sk6 等 drop 原因顯式列出。
 *
 * ⚠️ 已知限制（誠實標註，待 §5.6 P2 抽單一共用 pure predicate + contract test）：
 *  - `single-inbound-sk6` 與 `daita` bypass 需解析節點 `config` JSON，採 best-effort
 *    解析；config 缺漏/非法時比照後端走 fail-closed（sk6 視為 single-inbound）。
 *  - per-user 計量（`perUserMeteringEnabled`）在後端**不會 drop 節點**（只覆寫憑證），
 *    故此處只當資訊性標記呈現，不列為 why-not gate。
 */

/** 矩陣每格的綜合狀態燈（對應 §5.4 / §7.2 六色 + 全 gate 標示）。 */
export type CellStatus =
  | 'ok' // ✅ 可用（此品牌有方案選此節點且通過所有 gate）
  | 'unsel' // 🟡 品牌可見・方案未選
  | 'invisible' // ⛔ 品牌不可見（allowedBrands 顯式不含此品牌）
  | 'drift' // ⚠️ 漂移（在方案白名單內卻對品牌不可見）
  | 'shared' // 🌐 全品牌共享（NULL/空 allowedBrands，fail-OPEN）
  | 'gate'; // ⬛ 其他 gate 擋（health=down / minTier / isDecoy / nodeType / single-inbound）

/** 矩陣需要的節點欄位子集（取自 /api/admin/nodes 的 NodeRecord）。 */
export interface MatrixNode {
  id: number;
  name: string;
  displayName?: string | null;
  region?: string;
  ip?: string;
  port?: number;
  nodeType?: 'sk6' | 'vless';
  isDecoy?: boolean;
  isEnabled?: boolean;
  healthStatus?: string;
  minTier?: number;
  allowedBrands?: string[] | null;
  /** 節點 config（JSON 字串）— 用於 best-effort 推導 single-inbound / daita / 計量。 */
  config?: string | null;
}

/** 矩陣需要的方案（tier）欄位子集（取自 /api/admin/tiers）。 */
export interface MatrixTier {
  id: number;
  name: string;
  level: number;
  brand: string;
  canAccessVless?: boolean;
  canAccessDaita?: boolean;
  allowedNodeIds?: number[] | null;
}

export interface CellResult {
  status: CellStatus;
  /** 是否為 fail-OPEN 全品牌共享節點（NULL/空 allowedBrands）。 */
  shared: boolean;
  /** 人話 why / why-not 說明（顯示在 Popover；至少一行）。 */
  reasons: string[];
  /** 真正會讓 APP 看到此節點的方案 id（通過全部 gate）。 */
  qualifyingTierIds: number[];
}

const BRAND_CODE_PATTERN = /^[a-z][a-z0-9_]{1,15}$/;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * 鏡像後端 `normalizeAllowedBrands`：null / 非陣列 / 過濾後空陣列 → null（＝全品牌共享）。
 */
export function normalizeAllowedBrands(value: unknown): string[] | null {
  if (value == null) return null;
  if (!Array.isArray(value)) return null;
  const brands = value.filter(
    (item): item is string =>
      typeof item === 'string' && BRAND_CODE_PATTERN.test(item)
  );
  return brands.length > 0 ? Array.from(new Set(brands)) : null;
}

/** 鏡像後端 `isBrandAllowed`：NULL/空＝全可見（fail-OPEN），否則 includes。 */
export function isBrandAllowed(allowedBrands: unknown, brand: string): boolean {
  const normalized = normalizeAllowedBrands(allowedBrands);
  return normalized == null || normalized.includes(brand);
}

/** 是否為全品牌共享（fail-OPEN）節點。 */
export function isSharedAllBrands(allowedBrands: unknown): boolean {
  return normalizeAllowedBrands(allowedBrands) == null;
}

function parseNodeConfig(node: MatrixNode): Record<string, unknown> | null {
  const raw = node.config;
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isPlainObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** best-effort：節點是否開啟 DAITA（config.daita.enabled === true）。供 tier daita bypass 用。 */
export function isDaitaEnabled(node: MatrixNode): boolean {
  const config = parseNodeConfig(node);
  const daita = config && isPlainObject(config.daita) ? config.daita : null;
  return !!daita && (daita as { enabled?: unknown }).enabled === true;
}

/** best-effort：節點是否開啟 per-user 計量（僅資訊性標記，後端不據此 drop）。 */
export function isPerUserMeteringEnabled(node: MatrixNode): boolean {
  const config = parseNodeConfig(node);
  return (
    !!config &&
    (config as { perUserMeteringEnabled?: unknown }).perUserMeteringEnabled ===
      true
  );
}

/**
 * best-effort 鏡像後端 `isSingleInboundSk6Node`：sk6 節點若無任何多協議 fallback
 * （protocols[] / anytls.enabled / shadowtls.enabled / cdnDomain）且未顯式標記
 * `isReturnHomeNode`，後端會在後置過濾把它從 /api/ip drop 掉。config 缺漏/非法 → fail-closed。
 */
export function isSingleInboundSk6(node: MatrixNode): boolean {
  if ((node.nodeType ?? 'sk6') !== 'sk6') return false;
  const config = parseNodeConfig(node);
  if (!config || Object.keys(config).length === 0) return true;
  if ((config as { isReturnHomeNode?: unknown }).isReturnHomeNode === true) {
    return false;
  }

  const protocolsArr = Array.isArray(config.protocols)
    ? (config.protocols as unknown[])
    : [];
  const hasProtocolsFallback = protocolsArr.some(
    (p) => isPlainObject(p) && typeof (p as { type?: unknown }).type === 'string'
  );
  const anytlsEnabled =
    isPlainObject(config.anytls) &&
    (config.anytls as { enabled?: unknown }).enabled === true;
  const shadowtlsEnabled =
    isPlainObject(config.shadowtls) &&
    (config.shadowtls as { enabled?: unknown }).enabled === true;
  const cdnRelay = isPlainObject(config.cdnRelay)
    ? (config.cdnRelay as { domain?: unknown })
    : undefined;
  const cdnDomain =
    (typeof cdnRelay?.domain === 'string' && cdnRelay.domain.trim() !== '') ||
    (typeof config.cdnDomain === 'string' &&
      (config.cdnDomain as string).trim() !== '');

  return (
    !hasProtocolsFallback && !anytlsEnabled && !shadowtlsEnabled && !cdnDomain
  );
}

/**
 * 節點層級「硬 gate」：不分品牌 / 方案一律從 /api/ip drop。回傳 why-not 文字，未被擋回 null。
 * 順序對齊後端 getEnabledIPs：isEnabled → isDecoy → healthStatus=down → single-inbound-sk6。
 */
export function nodeHardGateReason(node: MatrixNode): string | null {
  if (node.isEnabled === false) return '節點已停用（isEnabled=false）';
  if (node.isDecoy === true) return '蜜罐節點（isDecoy）— 絕不下發給 APP';
  if ((node.healthStatus ?? '').toLowerCase() === 'down') {
    return '健康檢查 down（healthStatus=down）';
  }
  if (isSingleInboundSk6(node)) {
    return 'single-inbound SK6（無多協議 fallback）— 後端後置過濾 drop';
  }
  return null;
}

/** 鏡像後端 applyTierAllowedNodesFilter：此方案的白名單是否「選中」此節點。 */
export function tierSelectsNode(tier: MatrixTier, node: MatrixNode): boolean {
  const ids = tier.allowedNodeIds;
  // allow-all：欄位未設（null/undefined）或非陣列
  if (ids == null) return true;
  if (!Array.isArray(ids)) return true;
  // deny-all sentinel：[] = 整個方案鎖死（不 honour vless/daita bypass）
  if (ids.length === 0) return false;

  const valid = Array.from(
    new Set(
      ids.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)
    )
  );
  if (valid.length === 0) return false; // 全非法 → fail-closed
  if (valid.includes(node.id)) return true;
  // vless bypass：canAccessVless 方案下 vless 節點無條件納入
  if (tier.canAccessVless && (node.nodeType ?? 'sk6') === 'vless') return true;
  // daita bypass：canAccessDaita 方案下 daita 節點無條件納入
  if (tier.canAccessDaita && isDaitaEnabled(node)) return true;
  return false;
}

/** minTier gate：node.minTier <= tier.level。 */
function tierMinTierOk(tier: MatrixTier, node: MatrixNode): boolean {
  return (node.minTier ?? 0) <= tier.level;
}

/** nodeType gate：canAccessVless=false 的方案看不到 vless 節點。 */
function tierNodeTypeOk(tier: MatrixTier, node: MatrixNode): boolean {
  return !!tier.canAccessVless || (node.nodeType ?? 'sk6') === 'sk6';
}

/**
 * 計算「節點 × 品牌」一格的綜合 effective 狀態。
 *
 * 精準度：與 APP `/api/ip` 下發集合同語意（best-effort 鏡像）。狀態以嚴格優先序取最關鍵信號，
 * 完整 why-not 列在 `reasons`；🌐 全品牌共享同時以節點列 tag 永久呈現（見 index.tsx），
 * 故即使某格因 health/minTier 改顯 `gate`，operator 仍看得到「此節點全品牌共享」的事實。
 *
 * @param brandTiers 該品牌底下的所有方案（已按品牌過濾）。
 */
export function evaluateCell(
  node: MatrixNode,
  brandTiers: MatrixTier[],
  brand: string
): CellResult {
  const shared = isSharedAllBrands(node.allowedBrands);

  // 1) 節點層級硬 gate（不分品牌 / 方案，最高優先）。
  const hard = nodeHardGateReason(node);
  if (hard) {
    return { status: 'gate', shared, reasons: [hard], qualifyingTierIds: [] };
  }

  // 2) 品牌可見性（走 isBrandAllowed，NULL=全可見）。
  const visible = isBrandAllowed(node.allowedBrands, brand);
  const selectedTiers = brandTiers.filter((t) => tierSelectsNode(t, node));

  if (!visible) {
    // 顯式 allowedBrands 不含此品牌。
    if (selectedTiers.length > 0) {
      return {
        status: 'drift',
        shared: false,
        reasons: [
          `漂移：${selectedTiers.length} 個方案的白名單已選此節點，但節點 allowedBrands 不含「${brand}」→ APP 仍看不到`,
        ],
        qualifyingTierIds: [],
      };
    }
    return {
      status: 'invisible',
      shared: false,
      reasons: [`節點 allowedBrands 不含「${brand}」→ 對此品牌不可見`],
      qualifyingTierIds: [],
    };
  }

  // 3) 可見（顯式含此品牌 或 fail-OPEN 全品牌共享）→ 評估方案是否真的下發此節點。
  const qualifying = selectedTiers.filter(
    (t) => tierMinTierOk(t, node) && tierNodeTypeOk(t, node)
  );

  if (qualifying.length > 0) {
    const reason = `${qualifying.length} 個方案選此節點且通過全部 gate（${qualifying
      .map((t) => t.name)
      .join('、')}）`;
    return {
      status: shared ? 'shared' : 'ok',
      shared,
      reasons: [shared ? `全品牌共享(NULL)；此品牌可用 — ${reason}` : reason],
      qualifyingTierIds: qualifying.map((t) => t.id),
    };
  }

  // 4) 可見但沒有任何方案能下發 → 找出 why-not。
  if (selectedTiers.length > 0) {
    // 有方案選了此節點，但全被 minTier / nodeType 擋（避免 false-green）。
    const reasons = selectedTiers.map((t) => {
      const why: string[] = [];
      if (!tierMinTierOk(t, node)) {
        why.push(`節點 minTier=${node.minTier ?? 0} > 方案 level=${t.level}`);
      }
      if (!tierNodeTypeOk(t, node)) {
        why.push('VLESS 節點需方案 canAccessVless=true');
      }
      return `方案「${t.name}」已選此節點，但${why.join('；') || '被其他 gate 擋'}`;
    });
    return { status: 'gate', shared, reasons, qualifyingTierIds: [] };
  }

  // 沒有任何方案選此節點。
  if (shared) {
    // 🔴 §5.6 false-green 修補（鏡像後端 node_effective.evaluateCell）：全品牌共享但
    // 「此品牌無任何方案選用」＝ /api/ip 不會下發、APP 對此品牌看不到。不可回正向 'shared'
    // （🌐 會被當可用＝false-green，與後端 issue 3 同源），回 'unsel'（品牌可見・方案未選）。
    // 「全品牌共享」事實由 shared=true 旗標獨立呈現（前端以 node-row tag，不靠狀態燈）。
    return {
      status: 'unsel',
      shared: true,
      reasons: [
        '全品牌共享(NULL/空 allowedBrands)，但此品牌目前無方案選用此節點 → APP 看不到（不算可下發）',
      ],
      qualifyingTierIds: [],
    };
  }
  return {
    status: 'unsel',
    shared: false,
    reasons: [`對「${brand}」可見，但此品牌無方案選此節點 → APP 看不到`],
    qualifyingTierIds: [],
  };
}

/**
 * 漂移統計（供頂部橫幅）。漂移＝「在方案白名單內卻對品牌不可見」，**走 isBrandAllowed
 * 判定**（NULL/空 allowedBrands 全品牌共享，不算漂移）。
 */
export function countDrift(
  nodes: MatrixNode[],
  tiersByBrand: Map<string, MatrixTier[]>,
  brands: string[]
): number {
  let count = 0;
  for (const node of nodes) {
    for (const brand of brands) {
      const cell = evaluateCell(node, tiersByBrand.get(brand) ?? [], brand);
      if (cell.status === 'drift') count += 1;
    }
  }
  return count;
}
